import { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiCopy, FiDownload, FiHelpCircle, FiRefreshCw, FiSlash } from 'react-icons/fi';
import type { ImportDiscardedRow, ImportDuplicateRow, ImportIndeterminateRow, ImportOpcoesDestino, ImportRowCorrection } from '../../../types/imports';
import { Button } from '../../ui/Button';
import { SearchableSelect } from '../../ui';
import { carregarProgresso, salvarProgresso } from '../progresso';
import styles from './ImportResolutionWizard.module.css';

function formatDate(iso: string | null): string {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('pt-BR');
}

function formatValor(valor: number | null): string {
    if (valor === null || valor === undefined) return '-';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Mascara de moeda do campo Valor da correcao.
 *
 * O sinal e preservado: numa linha com problema ele ainda pode ser o unico
 * indicio do tipo. Quando o campo "Tipo" fica vazio, a regra R2 do backend
 * decide entrada ou saida pelo sinal do valor, entao engolir o menos mudaria
 * o lancamento de lado sem avisar.
 *
 * Digitacao acumula centavos da direita para a esquerda, igual as telas de
 * lancamento. O valor que veio da planilha NAO passa por esse acumulador --
 * ele ja e um numero pronto e seria reinterpretado como centavos, virando
 * -50,00 no lugar de -5.000,00.
 */
function formatarMoeda(valor: number): string {
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function mascararMoeda(texto: string): string {
    const negativo = texto.includes('-');
    // 15 digitos ja passa de um trilhao; alem disso Number perde precisao.
    const digitos = texto.replace(/\D/g, '').slice(0, 15);
    if (!digitos) return negativo ? '-' : '';
    const numero = Number(digitos) / 100;
    return `${negativo ? '-' : ''}${formatarMoeda(numero)}`;
}

/** Desfaz a mascara. Sempre 2 casas, entao os digitos sao centavos. */
function parsearMoeda(texto: string): number | undefined {
    const digitos = texto.replace(/\D/g, '').slice(0, 15);
    if (!digitos) return undefined;
    const numero = Number(digitos) / 100;
    return texto.includes('-') ? -numero : numero;
}

function toDateInputValue(iso: string | null): string {
    if (!iso) return '';
    return iso.slice(0, 10);
}

interface RowEdits {
    categoria: string;
    subCategoria: string;
    data: string;
    formaPagamento: string;
    valor: string;
    informacoesAdicionais: string;
}

function initialEdits(row: ImportDiscardedRow): RowEdits {
    return {
        categoria: row.categoriaOriginal ?? '',
        subCategoria: row.subCategoriaOriginal ?? '',
        data: toDateInputValue(row.data),
        formaPagamento: row.formaPagamentoOriginal ?? '',
        valor: row.valorOriginal !== null ? formatarMoeda(row.valorOriginal) : '',
        informacoesAdicionais: row.informacoesAdicionais ?? '',
    };
}

interface IndeterminateGroup {
    key: string;
    name: string;
    count: number;
}

type ResolutionStep =
    | { kind: 'indeterminate'; id: string; group: IndeterminateGroup }
    | { kind: 'error'; id: string; row: ImportDiscardedRow }
    | { kind: 'duplicate'; id: string; row: ImportDuplicateRow };

interface ImportResolutionWizardProps {
    /**
     * Id do lote. Serve de chave para guardar as correções digitadas, que
     * senão se perdem se o modal fechar antes do reenvio.
     */
    batchId?: string;

    /**
     * Plano de contas atual, para os campos de correção sugerirem o que já
     * existe em vez de aceitar qualquer texto. Opcional porque um preview
     * antigo pode não trazer.
     */
    opcoesDestino?: ImportOpcoesDestino;

    indeterminate: ImportIndeterminateRow[];
    subCategoryTypes: Record<string, 'ENTRADA' | 'SAIDA'>;
    onSetSubCategoryType: (key: string, tipo: 'ENTRADA' | 'SAIDA') => void;

    discardedRows: ImportDiscardedRow[];
    onRetryDiscarded: (corrections: ImportRowCorrection[]) => Promise<void>;
    onDownloadDiscarded?: () => Promise<void>;
    retryingDiscarded?: boolean;
    downloadingDiscarded?: boolean;
    /** Linhas que o usuário optou por deixar de fora em vez de corrigir. */
    ignoredRowNumbers: Set<number>;
    onToggleIgnoreRow: (rowNumber: number, ignore: boolean) => void;

    duplicateRows: ImportDuplicateRow[];
    keptDuplicateRowNumbers: Set<number>;
    reviewedDuplicateRowNumbers: Set<number>;
    onToggleKeepDuplicate: (rowNumber: number, keep: boolean) => void;
    /**
     * Atalho global: quando ligado, nenhuma duplicata é importada e as decisões
     * linha a linha ficam suspensas (útil para quem tem dezenas delas).
     */
    skipAllDuplicates: boolean;
    onSkipAllDuplicatesChange: (skip: boolean) => void;
}

/**
 * Valor do select que libera a digitação livre. A linha pode precisar de uma
 * conta ou forma de pagamento que ainda não existe, e a importação sabe criar;
 * fechar num select puro tiraria isso.
 */
const OUTRO = '__outro__';

const TIPO_CLASSE: Record<'' | 'Receita' | 'Despesa', (s: Record<string, string>) => string> = {
    '': () => '',
    Receita: (s) => s.tipoReceita,
    Despesa: (s) => s.tipoDespesa,
};

/**
 * Reduz o rotulo da coluna "Categoria" da planilha a uma das duas opcoes do
 * select. Espelha `resolveTypeFromCategoryLabel` do backend, para o campo nao
 * mostrar vazio quando a planilha trouxe algo como "GASTOS" ou "Despesas".
 */
function tipoSelecionado(valor: string): '' | 'Receita' | 'Despesa' {
    const normalizado = valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (normalizado.includes('gasto') || normalizado.includes('desp') || normalizado.includes('saida')) return 'Despesa';
    if (normalizado.includes('receita') || normalizado.includes('entrada')) return 'Receita';
    return '';
}

/**
 * Tipo que a linha terá depois de corrigida, seguindo a mesma ordem do
 * backend: o rótulo escolhido manda (R1) e, sem ele, o sinal do valor (R2).
 * É o que a cor do formulário mostra, para o usuário não confirmar achando
 * que lançou de um lado quando vai entrar no outro.
 */
function tipoEfetivo(edit: RowEdits | undefined): '' | 'Receita' | 'Despesa' {
    if (!edit) return '';
    const doRotulo = tipoSelecionado(edit.categoria);
    if (doRotulo) return doRotulo;
    return edit.valor.includes('-') ? 'Despesa' : '';
}

/**
 * Campo de escolha com busca, no padrão do resto da plataforma, mais a saída
 * "digitar outro" para o valor que ainda não existe no plano de contas.
 *
 * Antes era um `<input list>` com datalist. Funcionava, mas o balão nativo do
 * navegador é idêntico ao de autopreenchimento e o usuário não distinguia
 * sugestão do sistema de sugestão do browser.
 */
function CampoComBusca({
    valor,
    opcoes,
    onChange,
    idCampo,
    rotuloOutro,
}: {
    valor: string;
    opcoes: string[];
    onChange: (valor: string) => void;
    idCampo: string;
    rotuloOutro: string;
}) {
    // O campo abre sempre na lista. O valor que veio da planilha e ainda não
    // existe no plano de contas entra como opção própria, senão o campo
    // abriria vazio e o dado original sumiria da tela sem o usuário pedir.
    const [digitando, setDigitando] = useState(false);
    const semNaLista = valor.trim().length > 0 && !opcoes.includes(valor);
    const opcoesComValor = semNaLista ? [valor, ...opcoes] : opcoes;

    if (digitando) {
        return (
            <div className={styles.campoLivre}>
                <input
                    className={styles.fieldInput}
                    value={valor}
                    autoFocus
                    onChange={(e) => onChange(e.target.value)}
                />
                {opcoes.length > 0 && (
                    <button type="button" className={styles.campoLivreVoltar} onClick={() => setDigitando(false)}>
                        Escolher da lista
                    </button>
                )}
            </div>
        );
    }

    return (
        <SearchableSelect
            id={idCampo}
            className={styles.campoBusca}
            value={valor}
            options={[
                ...opcoesComValor.map((nome) => ({ value: nome, label: nome })),
                { value: OUTRO, label: rotuloOutro },
            ]}
            onChange={(escolhido) => {
                if (escolhido === OUTRO) {
                    setDigitando(true);
                    return;
                }
                onChange(escolhido);
            }}
            placeholder="Selecione ou digite para buscar"
            fullWidth
        />
    );
}

export function ImportResolutionWizard({
    batchId,
    opcoesDestino,
    indeterminate,
    subCategoryTypes,
    onSetSubCategoryType,
    discardedRows,
    onRetryDiscarded,
    onDownloadDiscarded,
    retryingDiscarded,
    downloadingDiscarded,
    ignoredRowNumbers,
    onToggleIgnoreRow,
    duplicateRows,
    keptDuplicateRowNumbers,
    reviewedDuplicateRowNumbers,
    onToggleKeepDuplicate,
    skipAllDuplicates,
    onSkipAllDuplicatesChange,
}: ImportResolutionWizardProps) {
    const [edits, setEdits] = useState<Record<number, RowEdits>>({});

    // Sugestoes vindas do plano de contas. Os campos seguem aceitando texto
    // livre de proposito: a linha pode precisar de uma conta que ainda nao
    // existe, e a importacao sabe criar. O datalist encurta o caminho comum
    // sem fechar o incomum.
    const categoriasSugeridas = useMemo(
        () => [...new Set((opcoesDestino?.categorias ?? []).map((c) => c.nome))].sort((a, b) => a.localeCompare(b)),
        [opcoesDestino],
    );
    const formasSugeridas = useMemo(
        () => [...new Set((opcoesDestino?.formasPagamento ?? []).map((f) => f.nome))].sort((a, b) => a.localeCompare(b)),
        [opcoesDestino],
    );

    useEffect(() => {
        // O que o usuário já tinha digitado vence o valor original da linha.
        const salvo = batchId ? carregarProgresso<Record<number, RowEdits>>(batchId, 'correcoes') : null;
        setEdits((current) => {
            let changed = false;
            const next = { ...current };
            for (const row of discardedRows) {
                if (!next[row.rowNumber]) {
                    next[row.rowNumber] = salvo?.[row.rowNumber] ?? initialEdits(row);
                    changed = true;
                }
            }
            return changed ? next : current;
        });
    }, [discardedRows, batchId]);

    useEffect(() => {
        if (!batchId || Object.keys(edits).length === 0) return;
        salvarProgresso(batchId, 'correcoes', edits);
    }, [batchId, edits]);

    const indeterminateGroups = useMemo<IndeterminateGroup[]>(() => {
        const map = new Map<string, IndeterminateGroup>();
        for (const row of indeterminate) {
            const key = row.normalizedKey || (row.subCategoriaOriginal || '').trim().toLowerCase();
            if (!key) continue;
            const existing = map.get(key);
            if (existing) existing.count += 1;
            else map.set(key, { key, name: row.subCategoriaOriginal || key, count: 1 });
        }
        return Array.from(map.values());
    }, [indeterminate]);

    const steps = useMemo<ResolutionStep[]>(() => {
        const list: ResolutionStep[] = [];
        for (const group of indeterminateGroups) list.push({ kind: 'indeterminate', id: `ind:${group.key}`, group });
        for (const row of discardedRows) list.push({ kind: 'error', id: `err:${row.rowNumber}`, row });
        for (const row of duplicateRows) list.push({ kind: 'duplicate', id: `dup:${row.rowNumber}`, row });
        return list;
    }, [indeterminateGroups, discardedRows, duplicateRows]);

    const [activeId, setActiveId] = useState<string | null>(steps[0]?.id ?? null);

    useEffect(() => {
        if (steps.length === 0) return;
        if (!steps.some((step) => step.id === activeId)) {
            setActiveId(steps[0].id);
        }
    }, [steps, activeId]);

    if (steps.length === 0) return null;

    // Fala só do que realmente apareceu nesta planilha, com número e no
    // vocabulário do usuário — nada de listar problemas que ele não tem.
    const pendingLabels: string[] = [];
    if (indeterminateGroups.length > 0) {
        pendingLabels.push(
            indeterminateGroups.length === 1
                ? '1 categoria que não sabemos se é receita ou despesa'
                : `${indeterminateGroups.length} categorias que não sabemos se são receita ou despesa`,
        );
    }
    if (discardedRows.length > 0) {
        pendingLabels.push(
            discardedRows.length === 1
                ? '1 linha com informação faltando ou inválida'
                : `${discardedRows.length} linhas com informação faltando ou inválida`,
        );
    }
    if (duplicateRows.length > 0) {
        pendingLabels.push(
            duplicateRows.length === 1
                ? '1 linha que parece já ter sido lançada'
                : `${duplicateRows.length} linhas que parecem já ter sido lançadas`,
        );
    }

    const activeIndex = Math.max(0, steps.findIndex((step) => step.id === activeId));
    const active = steps[activeIndex] ?? steps[0];

    const goTo = (index: number) => {
        const clamped = Math.max(0, Math.min(steps.length - 1, index));
        setActiveId(steps[clamped].id);
    };

    /**
     * Mantem Tipo e Valor coerentes entre si.
     *
     * Valor NEGATIVO e uma afirmacao de saida -- e a convencao da planilha a
     * partir da v7. Entao digitar negativo leva o Tipo para Despesa. Valor
     * positivo nao afirma nada: despesa lancada positiva e o formato antigo e
     * continua valida, por isso o caminho inverso nao existe.
     *
     * Escolher Receita com valor negativo e a mesma contradicao vista do outro
     * lado, e ali quem manda e a escolha explicita: o sinal cai.
     *
     * Sem isso o formulario aceitava Receita com -5.000,00 e o backend
     * resolvia calado -- a regra R1 (o rotulo) vence a R2 (o sinal), entao
     * entrava como receita de 5.000,00 e o menos era descartado sem aviso.
     */
    const updateField = (row: ImportDiscardedRow, field: keyof RowEdits, value: string) => {
        setEdits((current) => {
            const atual = current[row.rowNumber] ?? initialEdits(row);
            const proximo: RowEdits = { ...atual, [field]: value };

            if (field === 'valor') {
                if (value.includes('-')) {
                    // Negativo e afirmacao de saida; sobrepoe o que estava la.
                    proximo.categoria = 'Despesa';
                } else if (!tipoSelecionado(proximo.categoria) && value.trim() !== '') {
                    // Tipo vazio: mostra o que a regra R2 do backend faria com
                    // esse sinal, em vez de deixar o campo em branco e o
                    // usuario adivinhando de que lado a linha vai entrar.
                    proximo.categoria = 'Receita';
                }
            }

            if (field === 'categoria' && tipoSelecionado(value) === 'Receita' && proximo.valor.includes('-')) {
                proximo.valor = proximo.valor.replace('-', '');
            }

            return { ...current, [row.rowNumber]: proximo };
        });
    };

    const handleRetryDiscarded = async () => {
        // Linhas marcadas como ignoradas ficam de fora do reenvio: reprocessá-las
        // só as traria de volta com o mesmo problema.
        const corrections: ImportRowCorrection[] = discardedRows
            .filter((row) => !ignoredRowNumbers.has(row.rowNumber))
            .map((row) => {
                const edit = edits[row.rowNumber] ?? initialEdits(row);
                return {
                    rowNumber: row.rowNumber,
                    categoria: edit.categoria,
                    subCategoria: edit.subCategoria,
                    data: edit.data || undefined,
                    formaPagamento: edit.formaPagamento,
                    valor: parsearMoeda(edit.valor),
                    informacoesAdicionais: edit.informacoesAdicionais,
                };
            });
        // O backend recusa uma chamada sem correções; se sobrou só linha ignorada
        // não há o que reprocessar.
        if (corrections.length === 0) return;
        await onRetryDiscarded(corrections);
    };

    return (
        <div className={styles.wizard}>
            <div className={styles.intro}>
                <strong className={styles.introTitle}>
                    {pendingLabels.length === 1
                        ? 'Encontramos um ponto que precisa da sua resposta'
                        : `Encontramos ${pendingLabels.length} pontos que precisam da sua resposta`}
                </strong>
                <ul className={styles.introList}>
                    {pendingLabels.map((item) => <li key={item}>{item}</li>)}
                </ul>
                <span className={styles.introFootnote}>Nada é gravado até você confirmar a importação.</span>
            </div>

            <div className={styles.header}>
                <div className={styles.title}>{steps.length} it{steps.length === 1 ? 'em' : 'ens'} para revisar</div>
                <div className={styles.progress}>{activeIndex + 1} de {steps.length}</div>
            </div>

            <div className={styles.body}>
                <div className={styles.index}>
                    {indeterminateGroups.length > 0 && (
                        <div className={styles.indexGroup}>
                            <div className={styles.indexGroupLabel}><FiHelpCircle /> Tipo indefinido ({indeterminateGroups.length})</div>
                            {indeterminateGroups.map((group) => {
                                const id = `ind:${group.key}`;
                                const tipo = subCategoryTypes[group.key];
                                return (
                                    <button key={id} type="button" className={`${styles.indexChip} ${id === active.id ? styles.indexChipActive : ''}`} onClick={() => setActiveId(id)}>
                                        <span className={styles.indexChipLabel}>{group.name}</span>
                                        {tipo && <span className={`${styles.tag} ${tipo === 'ENTRADA' ? styles.tagEntrada : styles.tagSaida}`}>{tipo === 'ENTRADA' ? 'Receita' : 'Despesa'}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {discardedRows.length > 0 && (
                        <div className={styles.indexGroup}>
                            <div className={styles.indexGroupLabel}><FiAlertTriangle /> Linhas com problema ({discardedRows.length})</div>
                            {discardedRows.map((row) => {
                                const id = `err:${row.rowNumber}`;
                                const ignored = ignoredRowNumbers.has(row.rowNumber);
                                const tipoChip = tipoEfetivo(edits[row.rowNumber]);
                                return (
                                    <button key={id} type="button" className={`${styles.indexChip} ${id === active.id ? styles.indexChipActive : ''}`} onClick={() => setActiveId(id)}>
                                        {tipoChip && (
                                            <span
                                                className={tipoChip === 'Receita' ? styles.pontoReceita : styles.pontoDespesa}
                                                title={tipoChip === 'Receita' ? 'Receita' : 'Despesa'}
                                                aria-hidden
                                            />
                                        )}
                                        <span className={styles.indexChipLabel}>Linha {row.rowNumber}</span>
                                        {ignored && <span className={`${styles.tag} ${styles.tagSkip}`}>ignorada</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {duplicateRows.length > 0 && (
                        <div className={styles.indexGroup}>
                            <div className={styles.indexGroupLabel}><FiCopy /> Duplicatas potenciais ({duplicateRows.length})</div>
                            {duplicateRows.map((row) => {
                                const id = `dup:${row.rowNumber}`;
                                const kept = !skipAllDuplicates && keptDuplicateRowNumbers.has(row.rowNumber);
                                const reviewed = skipAllDuplicates || reviewedDuplicateRowNumbers.has(row.rowNumber);
                                return (
                                    <button key={id} type="button" className={`${styles.indexChip} ${id === active.id ? styles.indexChipActive : ''}`} onClick={() => setActiveId(id)}>
                                        <span className={styles.indexChipLabel}>Linha {row.rowNumber}</span>
                                        {reviewed && (
                                            kept
                                                ? <span className={`${styles.tag} ${styles.tagKeep}`}>importar</span>
                                                : <span className={`${styles.tag} ${styles.tagSkip}`}>não importar</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.card}>
                    {active.kind === 'indeterminate' && (
                        <>
                            <div className={styles.cardKicker}>Tipo indefinido</div>
                            <h3 className={styles.cardTitle}>"{active.group.name}" é receita ou despesa?</h3>
                            <p className={styles.cardHint}>
                                Essa categoria aparece em {active.group.count} linha{active.group.count === 1 ? '' : 's'} da planilha e não conseguimos identificar automaticamente se é dinheiro entrando ou saindo.
                            </p>
                            <div className={styles.bigChoices}>
                                <button
                                    type="button"
                                    className={`${styles.bigChoiceBtn} ${subCategoryTypes[active.group.key] === 'ENTRADA' ? styles.bigChoiceBtnActiveEntrada : ''}`}
                                    onClick={() => onSetSubCategoryType(active.group.key, 'ENTRADA')}
                                >
                                    💰 É uma Receita
                                    <span>dinheiro entrando</span>
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.bigChoiceBtn} ${subCategoryTypes[active.group.key] === 'SAIDA' ? styles.bigChoiceBtnActiveSaida : ''}`}
                                    onClick={() => onSetSubCategoryType(active.group.key, 'SAIDA')}
                                >
                                    💸 É uma Despesa
                                    <span>dinheiro saindo</span>
                                </button>
                            </div>
                        </>
                    )}

                    {active.kind === 'error' && (() => {
                        const row = active.row;
                        const edit = edits[row.rowNumber] ?? initialEdits(row);
                        const ignored = ignoredRowNumbers.has(row.rowNumber);
                        const tipoDaLinha = tipoEfetivo(edit);
                        return (
                            <>
                                <div className={styles.cardKicker}>Linha com problema</div>
                                <div className={styles.tituloComSelo}>
                                    <h3 className={styles.cardTitle}>Linha {row.rowNumber}</h3>
                                    {tipoDaLinha && (
                                        <span className={tipoDaLinha === 'Receita' ? styles.seloReceita : styles.seloDespesa}>
                                            {tipoDaLinha}
                                        </span>
                                    )}
                                </div>
                                <p className={styles.motivo}>{row.motivo}</p>
                                {ignored && (
                                    <div className={styles.ignoredNote}>
                                        Esta linha não será importada. Você pode voltar atrás se quiser corrigi-la.
                                    </div>
                                )}
                                <div className={`${styles.fieldGrid} ${ignored ? styles.fieldGridDisabled : ''}`}>
                                    {/*
                                      * Este campo e a coluna "Categoria" da planilha, que so diz se a
                                      * linha e Gastos ou Receita. Texto livre aqui enganava: o backend
                                      * so entende rotulos reconheciveis, e qualquer outra coisa deixava
                                      * a linha sem tipo sem avisar ninguem.
                                      */}
                                    <label className={styles.fieldLabel}>Tipo
                                        <select
                                            className={`${styles.fieldInput} ${TIPO_CLASSE[tipoSelecionado(edit.categoria)](styles)}`}
                                            value={tipoSelecionado(edit.categoria)}
                                            onChange={(e) => updateField(row, 'categoria', e.target.value)}
                                        >
                                            <option value="">Selecione...</option>
                                            <option value="Receita">Receita</option>
                                            <option value="Despesa">Despesa</option>
                                        </select>
                                    </label>
                                    <label className={styles.fieldLabel}>Categoria
                                        <CampoComBusca
                                            idCampo={`categoria-${row.rowNumber}`}
                                            valor={edit.subCategoria}
                                            opcoes={categoriasSugeridas}
                                            onChange={(v) => updateField(row, 'subCategoria', v)}
                                            rotuloOutro="+ Digitar uma categoria nova"
                                        />
                                    </label>
                                    <label className={styles.fieldLabel}>Data
                                        <input type="date" className={styles.fieldInput} value={edit.data} onChange={(e) => updateField(row, 'data', e.target.value)} />
                                    </label>
                                    <label className={styles.fieldLabel}>Forma de pagamento
                                        <CampoComBusca
                                            idCampo={`forma-${row.rowNumber}`}
                                            valor={edit.formaPagamento}
                                            opcoes={formasSugeridas}
                                            onChange={(v) => updateField(row, 'formaPagamento', v)}
                                            rotuloOutro="+ Digitar uma forma de pagamento nova"
                                        />
                                    </label>
                                    <label className={styles.fieldLabel}>Valor
                                        <div className={`${styles.moedaWrapper} ${tipoDaLinha === 'Receita' ? styles.moedaReceita : tipoDaLinha === 'Despesa' ? styles.moedaDespesa : ''}`}>
                                            <span className={styles.moedaPrefixo}>R$</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className={styles.moedaInput}
                                                value={edit.valor}
                                                placeholder="0,00"
                                                onChange={(e) => updateField(row, 'valor', mascararMoeda(e.target.value))}
                                            />
                                        </div>
                                    </label>
                                    <label className={styles.fieldLabel}>Informações adicionais
                                        <input className={styles.fieldInput} value={edit.informacoesAdicionais} onChange={(e) => updateField(row, 'informacoesAdicionais', e.target.value)} />
                                    </label>
                                </div>
                                <div className={styles.footer} style={{ borderTop: 'none', paddingTop: 0 }}>
                                    {onDownloadDiscarded && (
                                        <Button type="button" variant="outline" onClick={onDownloadDiscarded} disabled={downloadingDiscarded}>
                                            <FiDownload size={14} /> {downloadingDiscarded ? 'Baixando...' : 'Baixar planilha com erros'}
                                        </Button>
                                    )}
                                    <div className={styles.nav}>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => onToggleIgnoreRow(row.rowNumber, !ignored)}
                                        >
                                            <FiSlash size={14} /> {ignored ? 'Voltar a corrigir' : 'Ignorar lançamento'}
                                        </Button>
                                        <Button type="button" onClick={handleRetryDiscarded} disabled={retryingDiscarded || ignored}>
                                            <FiRefreshCw size={14} /> {retryingDiscarded ? 'Corrigindo...' : 'Importar'}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        );
                    })()}

                    {active.kind === 'duplicate' && (() => {
                        const row = active.row;
                        const kept = !skipAllDuplicates && keptDuplicateRowNumbers.has(row.rowNumber);
                        // Enquanto a linha não é respondida, nenhum dos dois botões fica
                        // destacado — senão "Não importar" pareceria já escolhido só por
                        // ser o padrão interno.
                        const decided = skipAllDuplicates || reviewedDuplicateRowNumbers.has(row.rowNumber);
                        return (
                            <>
                                <div className={styles.cardKicker}>Duplicata potencial</div>
                                <h3 className={styles.cardTitle}>A linha {row.rowNumber} já foi lançada antes?</h3>

                                <div className={styles.compare}>
                                    <div className={styles.compareCard}>
                                        <div className={styles.compareCardTitle}>Linha {row.rowNumber} da planilha</div>
                                        <div className={styles.compareRow}><span>Tipo</span><span>{row.categoriaOriginal || '-'}</span></div>
                                        <div className={styles.compareRow}><span>Categoria</span><span>{row.subCategoriaOriginal || '-'}</span></div>
                                        <div className={styles.compareRow}><span>Data</span><span>{formatDate(row.data)}</span></div>
                                        <div className={styles.compareRow}><span>Forma de pagamento</span><span>{row.formaPagamentoOriginal || '-'}</span></div>
                                        <div className={styles.compareRow}><span>Valor</span><span>{formatValor(row.valorOriginal)}</span></div>
                                        <div className={styles.compareRow}><span>Informações</span><span>{row.informacoesAdicionais || '-'}</span></div>
                                    </div>

                                    {row.duplicateOf?.kind === 'existing' ? (
                                        <div className={styles.compareCard}>
                                            <div className={styles.compareCardTitle}>Já existe no sistema</div>
                                            <div className={styles.compareRow}><span>Tipo</span><span>{row.duplicateOf.existingLancamento.categoria === 'RECEITA' ? 'Receita' : 'Despesa'}</span></div>
                                            <div className={styles.compareRow}><span>Categoria</span><span>{row.duplicateOf.existingLancamento.subCategoria}</span></div>
                                            <div className={styles.compareRow}><span>Data</span><span>{formatDate(row.duplicateOf.existingLancamento.data)}</span></div>
                                            <div className={styles.compareRow}><span>Forma de pagamento</span><span>{row.duplicateOf.existingLancamento.formaPagamento}</span></div>
                                            <div className={styles.compareRow}><span>Valor</span><span>{formatValor(row.duplicateOf.existingLancamento.valor)}</span></div>
                                            <div className={styles.compareRow}><span>Informações</span><span>{row.duplicateOf.existingLancamento.informacoesAdicionais || '-'}</span></div>
                                        </div>
                                    ) : row.duplicateOf?.kind === 'sameFile' ? (
                                        <div className={styles.compareCard}>
                                            <div className={styles.compareCardTitle}>Nesta mesma planilha</div>
                                            <div className={styles.sameFileNote}>Idêntica à linha {row.duplicateOf.rowNumber} deste mesmo arquivo.</div>
                                        </div>
                                    ) : null}
                                </div>

                                <div className={`${styles.duplicateChoices} ${skipAllDuplicates ? styles.duplicateChoicesDisabled : ''}`}>
                                    <button
                                        type="button"
                                        className={`${styles.duplicateChoiceBtn} ${decided && !kept ? styles.duplicateChoiceBtnActive : ''}`}
                                        onClick={() => onToggleKeepDuplicate(row.rowNumber, false)}
                                        disabled={skipAllDuplicates}
                                    >
                                        Não importar
                                        <span>é a mesma coisa (recomendado)</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.duplicateChoiceBtn} ${decided && kept ? styles.duplicateChoiceBtnActive : ''}`}
                                        onClick={() => onToggleKeepDuplicate(row.rowNumber, true)}
                                        disabled={skipAllDuplicates}
                                    >
                                        Importar
                                        <span>são coisas diferentes</span>
                                    </button>
                                </div>

                                {duplicateRows.length > 1 && (
                                    <div className={styles.bulkBox}>
                                        <label className={styles.bulkCheckbox}>
                                            <input
                                                type="checkbox"
                                                checked={skipAllDuplicates}
                                                onChange={(e) => onSkipAllDuplicatesChange(e.target.checked)}
                                            />
                                            <span>Ignorar duplicatas potenciais</span>
                                        </label>
                                        <p className={styles.bulkText}>
                                            {skipAllDuplicates
                                                ? `Nenhuma das ${duplicateRows.length} linhas marcadas como duplicata será importada. Desmarque se quiser decidir uma por uma.`
                                                : `São ${duplicateRows.length} duplicatas. Se você não quiser decidir uma por uma, marque a opção acima: todas ficam de fora da importação e você pode seguir direto.`}
                                        </p>
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    <div className={styles.footer}>
                        <div />
                        <div className={styles.nav}>
                            <Button type="button" variant="outline" onClick={() => goTo(activeIndex - 1)} disabled={activeIndex === 0}>
                                <FiChevronLeft /> Anterior
                            </Button>
                            <Button type="button" variant="outline" onClick={() => goTo(activeIndex + 1)} disabled={activeIndex === steps.length - 1}>
                                Próxima <FiChevronRight />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
